import {
	ChannelsScreen,
	ChannelsSkeleton,
} from "@commerce/channels/ChannelsScreen";
import { numbersQuery } from "@commerce/channels/queries";
import { channelsSearchSchema } from "@commerce/channels/search-params";
import { Page } from "@commerce/components/page";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AdminOnlyNotice } from "@saas/settings/components/SettingsLayout";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/channels/",
)({
	validateSearch: channelsSearchSchema,
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
	}),
	// The connect dialog is client state; the loader only warms the numbers.
	shouldReload: false,
	loader: async ({ context }) => {
		const organizationId = context.organization.id;
		const queryClient = getServerQueryClient();
		if (context.isOrganizationAdmin) {
			await queryClient.ensureQueryData(numbersQuery(organizationId));
		}
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: () => ({ meta: [{ title: `Channels - ${config.appName}` }] }),
	component: ChannelsPage,
});

function ChannelsPage() {
	const { isOrganizationAdmin } = Route.useRouteContext();
	const { organizationId, dehydratedState } = Route.useLoaderData();

	if (!isOrganizationAdmin) {
		return (
			<Page width="narrow">
				<AdminOnlyNotice what="channels" />
			</Page>
		);
	}
	return (
		<AsyncBoundary
			fallback={<ChannelsSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<ChannelsScreen organizationId={organizationId} />
		</AsyncBoundary>
	);
}
